import React, { useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react'; // Added X
import { colorClasses, badgeBaseClass, marathiLabels } from '../utils/categoryStyles';

const MobileFilterDropdown = ({
  category,
  label,
  options,
  filteredOptions, // ✅ new optional prop for dynamic options
  icon,
  color = 'gray',
  openCategory,
  setOpenCategory,
  filters,
  setFilters,
  disabledOptions = [], // New prop
}) => {
  const isOpen = openCategory === category;
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenCategory(null);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleBadgeClick = (value) => {
    const lowerValue = value.toLowerCase(); // Work with lowercase values for consistency
    const current = filters[category] || [];
    const updated = current.includes(lowerValue)
      ? current.filter((v) => v !== lowerValue) // Assuming `v` in `current` is already lowercase
      : [...current, lowerValue];
    setFilters((prev) => ({ ...prev, [category]: updated.filter(Boolean) })); // Ensure no empty strings
  };
  const handleClear = () => {
    setFilters((prev) => ({ ...prev, [category]: [] }));
  };

  // ✅ choose filteredOptions if provided, else fallback to full options
  const displayOptions = filteredOptions || options;

  return (
    <>
      <style>
        {`@keyframes dropdown {
            0% { opacity: 0; transform: translateY(-4px) scale(0.98); }
            100% { opacity: 1; transform: translateY(0) scale(1); }
          }
          .animate-dropdown { animation: dropdown 150ms ease-out; }`}
      </style>
      <button
        onClick={() => setOpenCategory(isOpen ? null : category)}
        className={`w-full inline-flex items-center justify-between px-2 py-0.5 min-h-[36px] rounded-md text-xs font-medium shadow-sm transition-shadow focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-300 active:scale-95 ${colorClasses[color]}`}
        aria-expanded={isOpen}
        aria-controls={`dropdown-${category}`}
      >
        <div className="flex items-center gap-1.5 truncate">
          {icon && <span className="w-4 h-4">{icon}</span>}
          <span className="truncate">{label}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 ml-1 text-gray-600 transform transition-transform duration-300 rotate-180" aria-hidden="true" />
        ) : (
          <ChevronDown className="w-4 h-4 ml-1 text-gray-600 transform transition-transform duration-300" aria-hidden="true" />
        )}
      </button>

      {isOpen && (
        <div
          ref={dropdownRef} id={`dropdown-${category}`} className="w-full mt-1 bg-white border rounded-md shadow-lg z-50 p-1 animate-dropdown" style={{ position: 'relative' }}>
          <div className="flex justify-end mb-1">
            <button
              onClick={handleClear}
              className="text-xs text-blue-500 underline px-1 py-0.5 hover:text-blue-700 focus:outline-none"
            >
              Clear
            </button>
          </div>
          <div className="flex flex-wrap gap-x-0.5 gap-y-0.5 max-h-[60vh] overflow-y-scroll pr-1 pb-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100" role="listbox">
            {displayOptions.map((option) => {
              const normalizedOption = option.trim().toLowerCase();
              const isSelected = (filters[category] || []).includes(normalizedOption);
              const isDisabled = disabledOptions.includes(option);
              const labelOverride = (category === 'subtopic' || category === 'digits') && marathiLabels[normalizedOption] ? marathiLabels[normalizedOption] : option;
              return (
                <button
                  key={option}
                  onClick={() => !isDisabled && handleBadgeClick(option)}
                  aria-selected={isSelected}
                  aria-label={`${labelOverride} ${isSelected ? 'selected' : 'not selected'}`}
                  aria-disabled={isDisabled}
                  tabIndex={0}
                  role="option"
                  className={`py-0.5 px-1.5 min-h-[28px] rounded-full text-xs transition-transform duration-100 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400
                    ${isSelected
                      ? 'bg-blue-600 text-white font-semibold shadow-md active:scale-95'
                      : isDisabled
                        ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                        : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 focus:bg-gray-200 hover:shadow-sm active:scale-95'}
                  `}
                  disabled={isDisabled}
                >
                  {labelOverride}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default MobileFilterDropdown;
